/**
 * PhoneField — `+91` prefix + 10-digit national number, bound to RHF.
 *
 * The DOM displays a "5 5" digit grouping for legibility (e.g. `98765 43210`),
 * but the value the form sees is always 10 raw digits — matching the
 * `phoneIN` Zod regex in `@flint/api-contract`.
 *
 * @see prototype/index.html — S01 phone input
 * @see packages/api-contract/src/_common.ts — phoneIN
 */
import * as React from "react";
import { useFormContext, type FieldPath, type FieldValues } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "./form.js";
import { cn } from "../lib/cn.js";

export interface PhoneFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "name" | "defaultValue" | "value" | "onChange"
> {
  name: TName;
  label: React.ReactNode;
  helper?: React.ReactNode;
  /** Country prefix label. Default `+91` (India is the only V1 country). */
  prefix?: string;
}

function formatGroups(digits: string) {
  const d = digits.replace(/\D/g, "").slice(0, 10);
  return d.length > 5 ? `${d.slice(0, 5)} ${d.slice(5)}` : d;
}

export function PhoneField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helper,
  prefix = "+91",
  className,
  ...inputProps
}: PhoneFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField<TFieldValues, TName>
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <div
            className={cn(
              "flex items-stretch rounded-sm bg-bg border border-line overflow-hidden",
              "transition-colors hover:border-line-2",
              "focus-within:bg-paper focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100",
              fieldState.error && "border-bad",
            )}
          >
            <span
              aria-hidden
              className="inline-flex items-center px-3 text-ink-2 border-r border-line font-mono text-sm tabular-nums"
            >
              {prefix}
            </span>
            {/* `FormControl` (Slot) merges id/aria-* onto its single child so
                the label's htmlFor lands on the actual <input>, not the
                wrapper. Required for Playwright's getByLabel + screen-reader
                label association. */}
            <FormControl>
              <input
                {...inputProps}
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={11}
                placeholder="98765 43210"
                className={cn(
                  "flex-1 min-w-0 h-10 px-3 bg-transparent border-0 outline-hidden",
                  "font-mono text-sm tracking-wide text-ink placeholder:text-muted",
                  className,
                )}
                value={formatGroups(field.value ?? "")}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  field.onChange(digits);
                }}
                onBlur={field.onBlur}
                ref={field.ref}
                name={field.name}
              />
            </FormControl>
          </div>
          {helper ? <FormDescription>{helper}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
